import { createContext, useContext, useState, useEffect } from "react";

// Date filter context for global date range filtering
const DateFilterContext = createContext(null);

export const useDateFilter = () => {
  const context = useContext(DateFilterContext);
  if (!context) {
    throw new Error("useDateFilter must be used within a DateFilterProvider");
  }
  return context;
};

// Preset date ranges
export const DATE_PRESETS = {
  TODAY: "today",
  YESTERDAY: "yesterday",
  LAST_7_DAYS: "last_7_days",
  LAST_14_DAYS: "last_14_days",
  LAST_30_DAYS: "last_30_days",
  LAST_90_DAYS: "last_90_days",
  THIS_MONTH: "this_month",
  LAST_MONTH: "last_month",
  THIS_QUARTER: "this_quarter",
  LAST_QUARTER: "last_quarter",
  THIS_YEAR: "this_year",
  LAST_YEAR: "last_year",
  CUSTOM: "custom",
};

// Get date range from preset
const getDateRangeFromPreset = (preset) => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  
  switch (preset) {
    case DATE_PRESETS.TODAY:
      return { start: today, end: today };
    
    case DATE_PRESETS.YESTERDAY:
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      return { start: yesterday, end: yesterday };
    
    case DATE_PRESETS.LAST_7_DAYS:
      const last7 = new Date(today);
      last7.setDate(last7.getDate() - 7);
      return { start: last7, end: today };
    
    case DATE_PRESETS.LAST_14_DAYS:
      const last14 = new Date(today);
      last14.setDate(last14.getDate() - 14);
      return { start: last14, end: today };
    
    case DATE_PRESETS.LAST_30_DAYS:
      const last30 = new Date(today);
      last30.setDate(last30.getDate() - 30);
      return { start: last30, end: today };
    
    case DATE_PRESETS.LAST_90_DAYS:
      const last90 = new Date(today);
      last90.setDate(last90.getDate() - 90);
      return { start: last90, end: today };
    
    case DATE_PRESETS.THIS_MONTH:
      const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
      return { start: thisMonthStart, end: today };
    
    case DATE_PRESETS.LAST_MONTH:
      const lastMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const lastMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0);
      return { start: lastMonthStart, end: lastMonthEnd };
    
    case DATE_PRESETS.THIS_QUARTER:
      const currentQuarter = Math.floor(now.getMonth() / 3);
      const thisQuarterStart = new Date(now.getFullYear(), currentQuarter * 3, 1);
      return { start: thisQuarterStart, end: today };
    
    case DATE_PRESETS.LAST_QUARTER:
      const lastQuarter = Math.floor(now.getMonth() / 3) - 1;
      const lastQuarterYear = lastQuarter < 0 ? now.getFullYear() - 1 : now.getFullYear();
      const lastQuarterMonth = lastQuarter < 0 ? 9 : lastQuarter * 3;
      const lastQuarterStart = new Date(lastQuarterYear, lastQuarterMonth, 1);
      const lastQuarterEnd = new Date(lastQuarterYear, lastQuarterMonth + 3, 0);
      return { start: lastQuarterStart, end: lastQuarterEnd };
    
    case DATE_PRESETS.THIS_YEAR:
      const thisYearStart = new Date(now.getFullYear(), 0, 1);
      return { start: thisYearStart, end: today };
    
    case DATE_PRESETS.LAST_YEAR:
      const lastYearStart = new Date(now.getFullYear() - 1, 0, 1);
      const lastYearEnd = new Date(now.getFullYear() - 1, 11, 31);
      return { start: lastYearStart, end: lastYearEnd };
    
    default:
      // Default to last 30 days
      const defaultStart = new Date(today);
      defaultStart.setDate(defaultStart.getDate() - 30);
      return { start: defaultStart, end: today };
  }
};

// Format date for display
const formatDate = (date) => {
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

// Format date for API
const formatDateForAPI = (date) => {
  return date.toISOString().split("T")[0];
};

export const DateFilterProvider = ({ children }) => {
  const [preset, setPreset] = useState(DATE_PRESETS.LAST_30_DAYS);
  const [customRange, setCustomRange] = useState({ start: null, end: null });
  const [dateRange, setDateRange] = useState(() => getDateRangeFromPreset(DATE_PRESETS.LAST_30_DAYS));

  // Update date range when preset changes
  useEffect(() => {
    if (preset !== DATE_PRESETS.CUSTOM) {
      setDateRange(getDateRangeFromPreset(preset));
    }
  }, [preset]);

  // Set a preset
  const selectPreset = (newPreset) => {
    setPreset(newPreset);
    if (newPreset !== DATE_PRESETS.CUSTOM) {
      setDateRange(getDateRangeFromPreset(newPreset));
    }
  };

  // Set custom date range
  const setCustomDateRange = (start, end) => {
    setPreset(DATE_PRESETS.CUSTOM);
    setCustomRange({ start, end });
    setDateRange({ start, end });
  };

  // Get display label for current selection
  const getDisplayLabel = () => {
    const labels = {
      [DATE_PRESETS.TODAY]: "Today",
      [DATE_PRESETS.YESTERDAY]: "Yesterday",
      [DATE_PRESETS.LAST_7_DAYS]: "Last 7 days",
      [DATE_PRESETS.LAST_14_DAYS]: "Last 14 days",
      [DATE_PRESETS.LAST_30_DAYS]: "Last 30 days",
      [DATE_PRESETS.LAST_90_DAYS]: "Last 90 days",
      [DATE_PRESETS.THIS_MONTH]: "This month",
      [DATE_PRESETS.LAST_MONTH]: "Last month",
      [DATE_PRESETS.THIS_QUARTER]: "This quarter",
      [DATE_PRESETS.LAST_QUARTER]: "Last quarter",
      [DATE_PRESETS.THIS_YEAR]: "This year",
      [DATE_PRESETS.LAST_YEAR]: "Last year",
      [DATE_PRESETS.CUSTOM]: `${formatDate(dateRange.start)} - ${formatDate(dateRange.end)}`,
    };
    return labels[preset] || "Select date range";
  };

  const value = {
    preset,
    dateRange,
    customRange,
    selectPreset,
    setCustomDateRange,
    getDisplayLabel,
    formatDate,
    formatDateForAPI,
    // API-ready values
    startDate: formatDateForAPI(dateRange.start),
    endDate: formatDateForAPI(dateRange.end),
  };

  return (
    <DateFilterContext.Provider value={value}>
      {children}
    </DateFilterContext.Provider>
  );
};

export default DateFilterContext;
